import { hasSessionHint } from './cookie.util';
import { ThemeService } from '../services/theme.service';

/**
 * Ties the cached profile's lifetime to the session by clearing it at
 * bootstrap whenever the session hint cookie is absent.
 *
 * An untrusted session's refresh cookie is a *session* cookie that dies on a
 * full browser close, but the cached `user` profile in storage would
 * otherwise survive indefinitely — leaving a stale profile with no session
 * behind it. `connect_auth` is written with that exact same lifetime, so
 * "no session hint" is the correct proxy for "the session, if any, is
 * gone": clear the profile. When the hint IS present (including a trusted,
 * still-alive 90-day session), this is a complete no-op. The hint is read
 * through {@link hasSessionHint} so this clear, the auth interceptor's
 * refresh gate, and `AuthenticationService.isLoggedIn()` cannot drift apart.
 *
 * Also clears the `connect-theme` anti-flash mirror (see `ThemeService`) in
 * the same pass, so the stored state stays honest: no profile means no
 * resolved theme to mirror. This clear does NOT itself prevent the dark ->
 * light pre-paint flash, and must not be relied on for that — `ThemeService`'s
 * constructor calls `applyResolvedTheme()`, which re-writes the mirror during
 * this very same bootstrap, and in any case the mirror is read before the
 * bundle executes at all. Preventing the flash is the job of the cookie gate
 * on `index.html`'s inline pre-paint script, which only honours the mirror
 * while `connect_auth` is present. Nothing is lost by clearing it here: a
 * trusted session never reaches this function, and
 * `ThemeService.applyFromStoredUser()` re-applies the user's `darkModeEnabled`
 * and re-writes the mirror the instant they log in again.
 *
 * Must run before anything else that reads the stored user at bootstrap
 * (the ui-secrets/Google-Maps initializer's INTERNAL-role check, and
 * `ThemeService`'s own initial resolve) — see the registration order in
 * `app.config.ts`.
 *
 * Each storage operation is independently swallowed so a throwing storage
 * (privacy mode, quota, a mocked-out store in tests) can never break app
 * bootstrap; worst case is a stale key survives one extra session.
 */
export function clearCachedProfileIfSessionEnded(): void {
  if (hasSessionHint()) {
    return;
  }
  try {
    localStorage.removeItem('user');
  } catch {
    // Storage unavailable — ignore.
  }
  try {
    sessionStorage.removeItem('user');
  } catch {
    // Storage unavailable — ignore.
  }
  try {
    localStorage.removeItem(ThemeService.STORAGE_KEY);
  } catch {
    // Storage unavailable — ignore.
  }
}
